import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, CheckCircle2, ShieldCheck } from "lucide-react";
import { api, errMsg, fmt } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { COINS, CoinLogo } from "@/components/CryptoLogos";
import { CopyField } from "@/components/CopyField";
import { CountdownBadge } from "@/components/Countdown";

export const CoinPicker = ({ coin, setCoin }) => (
  <div className="grid grid-cols-2 gap-3">
    {COINS.map((c) => (
      <button
        key={c.id}
        type="button"
        data-testid={`coin-option-${c.id}`}
        onClick={() => setCoin(c.id)}
        className={`x-panel p-3 flex items-center gap-3 border-2 transition-colors ${coin === c.id ? "border-[var(--x-blue)]" : "border-transparent hover:bg-[var(--x-panel-2)]"}`}
      >
        <CoinLogo coin={c.id} size={34} />
        <div className="text-left">
          <div className="font-extrabold">{c.id}</div>
          <div className="text-[12px] text-[var(--x-muted)]">{c.name} · {c.net}</div>
        </div>
      </button>
    ))}
  </div>
);

const Waiting = ({ dep, onDone }) => {
  const { refresh } = useAuth();
  const [d, setD] = useState(dep);
  useEffect(() => {
    if (d.status !== "pending") return;
    const t = setInterval(async () => {
      try {
        const { data } = await api.get(`/wallet/deposit/${d.id}`);
        setD(data);
        if (data.status === "approved") {
          toast.success(`₺${fmt(data.amount_try)} bakiyenize eklendi`);
          refresh();
        }
      } catch { /* ignore */ }
    }, 10000);
    return () => clearInterval(t);
  }, [d.status, d.id, refresh]);

  if (d.status === "approved")
    return (
      <div data-testid="crypto-deposit-success" className="text-center py-8 x-rise">
        <CheckCircle2 size={64} className="mx-auto text-[var(--x-green)]" />
        <div className="text-2xl font-black mt-4">Yatırım Onaylandı</div>
        <div className="text-[var(--x-muted)] mt-1">₺{fmt(d.amount_try)} bakiyenize eklendi</div>
        {d.bonus_amount > 0 && (
          <div className="mt-2 text-[var(--x-green)] font-bold" data-testid="crypto-bonus-line">+₺{fmt(d.bonus_amount)} çevrimsiz kripto yatırım bonusu</div>
        )}
        <button onClick={onDone} className="x-btn x-btn-primary w-full mt-6">Tamam</button>
      </div>
    );

  return (
    <div className="space-y-4 x-rise" data-testid="crypto-deposit-waiting">
      <div className="x-panel p-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <CoinLogo coin={d.coin} size={38} />
          <div>
            <div className="font-extrabold">{d.coin} <span className="text-[12px] text-[var(--x-muted)]">TRC20 ağı</span></div>
            <div className="text-[13px] text-[var(--x-muted)]">₺{fmt(d.amount_try)} yatırım</div>
          </div>
        </div>
        <CountdownBadge iso={d.expires_at} testId="crypto-countdown" />
      </div>
      <CopyField label={`Gönderilecek Miktar (${d.coin})`} value={d.coin_amount} testId="crypto-amount" mono />
      <CopyField label="Cüzdan Adresi" value={d.address} testId="crypto-address" mono />
      <div className="rounded-md bg-[#f5a623]/10 border border-[#f5a623]/30 p-3 text-[13px] text-[#f5c46b] font-semibold">
        Lütfen <b>tam olarak {d.coin_amount} {d.coin}</b> gönderin. Farklı miktarlar otomatik eşleşmez.
      </div>
      <div className="flex items-center gap-2 text-sm text-[var(--x-muted)]">
        {d.status === "pending" ? (
          <>
            <Loader2 size={16} className="animate-spin text-[var(--x-blue)]" />
            Tronscan üzerinden ödeme otomatik kontrol ediliyor...
          </>
        ) : (
          <span className="text-[var(--x-red)] font-bold">İşlem süresi doldu veya iptal edildi.</span>
        )}
      </div>
    </div>
  );
};

export const DepositCrypto = ({ onDone }) => {
  const [cfg, setCfg] = useState(null);
  const [coin, setCoin] = useState("USDT");
  const [amount, setAmount] = useState("");
  const [dep, setDep] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/wallet/config").then((r) => setCfg(r.data));
  }, []);

  const rate = cfg ? (coin === "USDT" ? cfg.rate_usdt : cfg.rate_trx) : 0;
  const est = rate && amount ? Number(amount) / rate : 0;

  const submit = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/wallet/deposit/crypto", { coin, amount_try: Number(amount) });
      setDep(data);
    } catch (e) {
      toast.error(errMsg(e));
    }
    setLoading(false);
  };

  if (dep) return <Waiting dep={dep} onDone={onDone} />;

  return (
    <div className="space-y-5 x-rise">
      <div>
        <span className="x-label">Coin Seçin</span>
        <CoinPicker coin={coin} setCoin={setCoin} />
      </div>
      <div>
        <span className="x-label">Yatırım Tutarı (TL)</span>
        <div className="relative">
          <input data-testid="crypto-deposit-amount-input" type="number" inputMode="decimal" className="x-input pr-10" placeholder={`Min. ${cfg?.min_deposit ?? ""}`} value={amount} onChange={(e) => setAmount(e.target.value)} />
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--x-muted)] font-bold">₺</span>
        </div>
        {est > 0 && (
          <div className="mt-2 text-[13px] text-[var(--x-muted)]" data-testid="crypto-estimate">
            ≈ <b className="text-white">{fmt(est, 4)} {coin}</b> · 1 {coin} = ₺{fmt(rate)}
            {cfg?.rates_live && <span className="x-chip bg-[var(--x-green)]/15 text-[var(--x-green)] ml-2">Canlı Kur</span>}
          </div>
        )}
      </div>
      <button data-testid="crypto-deposit-submit-btn" disabled={!amount || loading} onClick={submit} className="x-btn x-btn-primary w-full py-3.5">
        {loading && <Loader2 size={18} className="animate-spin" />} Tamam
      </button>
      <div className="flex items-center gap-2 text-[12px] text-[var(--x-dim)]">
        <ShieldCheck size={14} /> Ödemeler Tronscan üzerinden otomatik doğrulanır ve onaylanır.
      </div>
    </div>
  );
};
